import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { Note } from './notes.types';

// Module-scope for stable identity across renders.
export const MENTIONED_USER_ASSOCIATION_TYPE_NAME =
  'ahoy_note_to_mentioned_user';
export const CREATOR_ASSOCIATION_TYPE_NAME = 'ahoy_note_to_creator';
export const VISIBLE_TO_USER_ASSOCIATION_TYPE_NAME =
  'ahoy_note_to_visible_user';
export const VISIBLE_TO_ORG_ASSOCIATION_TYPE_NAME = 'ahoy_note_visible_to_org';
export const OWNER_ASSOCIATION_TYPE_NAME = 'ahoy_note_to_owner';
export const MEETING_ASSOCIATION_TYPE_NAME = 'ahoy_note_to_meeting';

/**
 * The two "note-like" entity types: both carry the memo contract (title,
 * markdown body, optional due) and the same family of system associations
 * (owner, creator, stage, mentions, visibility). Surfaces shared by notes and
 * tasks — the linked-records section, the action cards, the composer — key
 * their association names off the record's entity type through
 * `getMemoAssociationNames` instead of assuming `ahoy_note`.
 */
export type MemoEntityType = 'ahoy_note' | 'ahoy_task';

export function isMemoEntityType(
  entityTypeName: string
): entityTypeName is MemoEntityType {
  return entityTypeName === 'ahoy_note' || entityTypeName === 'ahoy_task';
}

export interface MemoAssociationNames {
  owner: string;
  creator: string;
  /**
   * The record → stage edge. `null` for the note family — notes have no
   * stage/pipeline concept (epic #13640); only tasks carry a real value.
   */
  stage: string | null;
  mentionedUser: string;
  visibleUser: string;
  visibleOrg: string;
  /**
   * The record → pipeline edge (structural; never a user-facing link).
   * `null` for the note family, same reasoning as `stage`.
   */
  pipeline: string | null;
}

const NOTE_ASSOCIATION_NAMES: MemoAssociationNames = {
  owner: OWNER_ASSOCIATION_TYPE_NAME,
  creator: CREATOR_ASSOCIATION_TYPE_NAME,
  stage: null,
  mentionedUser: MENTIONED_USER_ASSOCIATION_TYPE_NAME,
  visibleUser: VISIBLE_TO_USER_ASSOCIATION_TYPE_NAME,
  visibleOrg: VISIBLE_TO_ORG_ASSOCIATION_TYPE_NAME,
  pipeline: null,
};

// Mirrors apps/crm_events/schemas: the ahoy_task association family is the
// ahoy_note family renamed (see task.constants.ts for the two the task-only
// surfaces already use).
const TASK_ASSOCIATION_NAMES: MemoAssociationNames = {
  owner: 'ahoy_task_to_owner',
  creator: 'ahoy_task_to_creator',
  stage: 'ahoy_task_to_task_pipeline_stage',
  mentionedUser: 'ahoy_task_to_mentioned_user',
  visibleUser: 'ahoy_task_to_visible_user',
  visibleOrg: 'ahoy_task_visible_to_org',
  pipeline: 'ahoy_task_to_task_pipeline',
};

export function getMemoAssociationNames(
  entityTypeName: MemoEntityType
): MemoAssociationNames {
  return entityTypeName === 'ahoy_task'
    ? TASK_ASSOCIATION_NAMES
    : NOTE_ASSOCIATION_NAMES;
}

export function extractMemoData(
  obj: CrmObject,
  ownerAssociationTypeName: string | null,
  stageAssociationTypeName: string | null = null
): Note {
  const props = obj.properties || {};
  const ownerAssoc = ownerAssociationTypeName
    ? obj.associations?.find(
        a => a.association_type_name === ownerAssociationTypeName
      )
    : null;
  const stageAssoc = stageAssociationTypeName
    ? obj.associations?.find(
        a => a.association_type_name === stageAssociationTypeName
      )
    : null;
  // Creator + visibility are stamped per family (ahoy_task_to_creator /
  // ahoy_task_visible_to_org for tasks), so they must be keyed off the record's
  // own type — reading the note names here made every task look private and
  // creator-less. Non-memo callers (none today) fall back to the note family.
  const names = isMemoEntityType(obj.entity_type_name)
    ? getMemoAssociationNames(obj.entity_type_name)
    : NOTE_ASSOCIATION_NAMES;
  const creatorAssoc = obj.associations?.find(
    a => a.association_type_name === names.creator
  );
  const is_private = !obj.associations?.some(
    a => a.association_type_name === names.visibleOrg
  );

  return {
    entity_id: obj.entity_id,
    title: getPropertyValue<string>(props.ahoy_title) || '',
    content: getPropertyValue<string>(props.ahoy_content) || '',
    created_at: getPropertyValue<string>(props.ahoy_created_at) || '',
    updated_at: getPropertyValue<string>(props.ahoy_updated_at) || '',
    owner_entity_id: ownerAssoc?.target_entity_id || null,
    last_event_seq: obj.last_event_seq,
    date: getPropertyValue<string>(props.ahoy_date),
    datetime: getPropertyValue<string>(props.ahoy_datetime),
    stage_entity_id: stageAssoc?.target_entity_id ?? null,
    creator_entity_id: creatorAssoc?.target_entity_id ?? null,
    is_private,
  };
}

/**
 * Map a note's "due" value to the ahoy_date / ahoy_datetime property pair.
 *
 * The note form emits a single due string: a `YYYY-MM-DD` calendar date when
 * no time is set, or a full ISO 8601 datetime (contains "T") when a time is
 * set. A date-only value stores only `ahoy_date`. A datetime value sends only
 * `ahoy_datetime` — `ahoy_date` is OMITTED (key absent), because the backend
 * NoteDueDateCascade derives it server-side for every write path, using the
 * note owner's timezone (falling back to the creator's, then UTC). The
 * frontend neither derives nor clears the date when a time is set; the
 * derived value arrives via the CRM websocket like any other cascade write.
 *
 * Clear semantics stay caller-owned (the cascade never fires on clears): pass
 * empty/undefined to clear both; a date-only value clears the stale datetime.
 */
export function dueProperties(value: string | null | undefined): {
  ahoy_date?: string | null;
  ahoy_datetime: string | null;
} {
  if (!value) {
    return { ahoy_date: null, ahoy_datetime: null };
  }
  if (!value.includes('T')) {
    return { ahoy_date: value, ahoy_datetime: null };
  }
  return { ahoy_datetime: value };
}

/** The memo's current due value as the form represents it: the precise
 * datetime when present, else the date-only value, else undefined. */
export function memoDueValue(
  note: Pick<Note, 'date' | 'datetime'> | null | undefined
): string | undefined {
  return note?.datetime || note?.date || undefined;
}

export type MemoPrivacy = 'private' | 'shared';

/** The memo's visibility as the value the privacy controls and endpoint use. */
export function memoPrivacy(memo: Pick<Note, 'is_private'>): MemoPrivacy {
  return memo.is_private ? 'private' : 'shared';
}

// Mirrors the backend privacy-toggle endpoint auth rule.
export function isOwnerOrCreator(
  note: Pick<Note, 'owner_entity_id' | 'creator_entity_id'>,
  userEntityId: string | null | undefined
): boolean {
  if (!userEntityId) {
    return false;
  }
  return (
    note.owner_entity_id === userEntityId ||
    note.creator_entity_id === userEntityId
  );
}

export function validateMentionedUserIds(
  mentionedIds: string[],
  validUserIds: Set<string>
): string[] {
  return mentionedIds.filter(id => validUserIds.has(id));
}
